import { useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { useGetEventsQuery, useCreateEventMutation } from "@/api/locationApi/event";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import EventListTable from "./EventListTable";
import EventFormDialog from "./EventFormDialog";
import { EventRecord, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function EventList() {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const { data: eventsData } = useGetEventsQuery(queryParams);
  const [createEvent] = useCreateEventMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: EventRecord) => {
    createEvent(data)
      .unwrap()
      .then(() => {
        toast.success(t("temporalActivity:event.createdSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, pagination: { ...prev.pagination, page } }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("temporalActivity:event.title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("temporalActivity:event.title")}
                </h1>
                <p className="text-gray-500">{t("temporalActivity:event.manage")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("temporalActivity:event.create")}
                </Button>
              )}
            </div>
          </div>

          <EventListTable
            data={eventsData?.items || []}
            pagination={
              eventsData?.pagination
                ? {
                    pageSize: eventsData.pagination.pageSize || 15,
                    pageIndex: eventsData.pagination.pageIndex ?? eventsData.pagination.page ?? 1,
                    totalPages: eventsData.pagination.totalPages ?? 1,
                    totalItems: eventsData.pagination.totalItems ?? eventsData.pagination.total ?? 0,
                  }
                : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <EventFormDialog open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSubmit={handleCreate} />
    </NavbarSidebarLayout>
  );
}
